import { httpsCallable } from "firebase/functions";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { fxs } from "../../../../../../utils/firestore";
import moment from "../../../../../../utils/app_moment";
import { DateFormats, IClient } from "intake_shared/src/types";
import { Button } from "../../../../bits/Button/Button";
import styles from "./SupportTickets.module.scss";
import Skeleton from "../../../../bits/Skeleton/Skeleton";
import EmptyListState from "../../../../bits/EmptyListState/EmptyListState";
import Table from "../../../../bits/Table/Table";
import SupportConversation from "../SupportConversation/SupportConversation";
import { Badge } from "../../../../bits/Badge/Badge";
import {
    getPriorityLabel,
    getStatusLabel,
    PRIORITY_COLORS,
    TICKETS_STATUS_COLORS,
} from "../../../../../../constants";

interface SupportTicketsProps {
    tickets: any;
    ticketsLoading: boolean;
    client: IClient;
}

export default function SupportTickets({
    tickets,
    ticketsLoading,
    client,
}: SupportTicketsProps) {
    const { push } = useHistory();
    const { addToast } = useToasts();
    const [showDialog, setShowDialog] = React.useState(false);
    const [selectedTicket, setSelectedTicket] = React.useState<any>();

    const exportGetSupportTicketsConversations = httpsCallable(
        fxs,
        "httpFunctionExportGetSupportTicketsConversations"
    );
    const [conversationsLoading, setConversationsLoading] = React.useState(false);
    const [conversations, setConversations] = React.useState<unknown>();

    const getSupportTicketsConversations = async (id: string) => {
        setConversationsLoading(true);
        const results = await exportGetSupportTicketsConversations(id || "");
        setConversations(results.data);
        setConversationsLoading(false);
    };
    const columns = React.useMemo(() => {
        return [
            {
                Header: "Created on",
                id: "created_on",
                accessor: (row) => (
                    <p>
                        {row?.created_at
                            ? `${moment(row?.created_at).format(
                                  DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                              )}`
                            : ""}
                    </p>
                ),
            },

            {
                Header: "ID",
                accessor: (row) => <p>{row?.id || "--"}</p>,
            },
            {
                Header: "Title",
                accessor: (row) => <p>{row?.subject || "--"}</p>,
            },

            {
                Header: "Client",
                accessor: (row) => (
                    <p>{`${client.first_name} ${client.last_name}`}</p>
                ),
            },
            {
                Header: "Status",
                accessor: (row) => (
                    <Badge
                        content={getStatusLabel(row?.status)}
                        bgColor={TICKETS_STATUS_COLORS[row?.status]}
                        textColor="white"
                    />
                ),
            },
            {
                Header: "Priority",
                accessor: (row) => (
                    <Badge
                        content={getPriorityLabel(row?.priority)}
                        bgColor={PRIORITY_COLORS[row?.priority]}
                        textColor="white"
                    />
                ),
            },
            {
                Header: "Tags",
                id: "tags",
                accessor: (row) => <p>{row?.tags?.join(", ") || "--"}</p>,
            },
            {
                Header: "Due by",
                id: "due_by",
                accessor: (row) => (
                    <p>
                        {row?.due_by
                            ? `${moment(row?.due_by).format(
                                  DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                              )}`
                            : "--"}
                    </p>
                ),
            },

            {
                Header: "Actions",
                accessor: (row) => (
                    <Button
                        variant="normal"
                        className={styles.viewButton}
                        onClick={() => {
                            setShowDialog(true);
                            getSupportTicketsConversations(row.id);
                            setSelectedTicket(row);
                        }}
                    >
                        View
                    </Button>
                ),
            },
        ];

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addToast, push]);
    return (
        <div>
            {ticketsLoading ? (
                <Skeleton count={3} />
            ) : !tickets || tickets?.length === 0 ? (
                <EmptyListState
                    emptyMessage="No Support Tickets Yet"
                    stateHelperText="There are no tickets to display. Tickets will appear here once communication begins."
                />
            ) : (
                <Table
                    stickyLastColumn
                    columns={columns}
                    data={tickets ? tickets : []}
                    allowSelect={false}
                />
            )}
            <SupportConversation
                isModalOpen={showDialog}
                setIsModalOpen={setShowDialog}
                selectedTicket={selectedTicket}
                conversationsLoading={conversationsLoading}
                client={client}
                conversations={conversations}
            />
        </div>
    );
}
